<template>
  <div>
    <v-row>
      <v-col cols="12">
        <h1 class="primary--text font-weight-medium">
          {{ getSecurityTransferType(securityTransfer.transferType) }}
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="6"
        v-if="securityTransfer.transferType !== 'Internal'"
      >
        <ValidationProvider
          :name="$t('transfer_title_transfer_financial_institution')"
          rules="required|max:50"
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="securityTransfer.financialInstitution"
            autocomplete="nope"
            :label="`${$t('transfer_title_transfer_financial_institution')} *`"
            hide-details="auto"
            maxlength="150"
            dense
            :error-messages="errors[0]"
          />
        </ValidationProvider>
      </v-col>
      <v-col cols="12" md="6">
        <ValidationProvider
          :name="$t('transfer_index_account')"
          :rules="
            `required|alpha_num|${
              securityTransfer.transferType === 'Internal' ? 'max:6' : 'max:50'
            }`
          "
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="securityTransfer.accountNumber"
            autocomplete="nope"
            :label="`${$t('transfer_index_account')} *`"
            hide-details="auto"
            :maxlength="securityTransfer.transferType === 'Internal' ? 6 : 50"
            v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
            dense
            :error-messages="errors[0]"
          />
        </ValidationProvider>
      </v-col>
      <template v-if="securityTransfer.transferType !== 'Receive'">
        <v-col
          v-if="showBeneficiariesSelect"
          class="d-flex flex-grow-1"
          cols="12"
        >
          <ValidationProvider
            :name="$t('transfer_name')"
            rules="required|max:150"
            v-slot="{ errors }"
            class="flex-grow-1"
          >
            <v-select
              :label="`${$t('transfer_name')} *`"
              v-model="securityTransfer.beneficiaryName"
              :items="beneficiaries"
              dense
              hide-details="auto"
              autocomplete="nope"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                v-if="lockedField"
                icon
                v-on="on"
                @click="handleUnlock"
                :disabled="!lockedField"
              >
                <v-icon>mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('modify_beneficiary') }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-else :cols="12" class="d-flex flex-grow-1">
          <ValidationProvider
            class="flex-grow-1"
            :name="$t('transfer_name')"
            rules="required|max:200"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="securityTransfer.beneficiaryName"
              ref="beneficiary"
              class="flex-grow-1"
              autocomplete="nope"
              hide-details="auto"
              maxlength="200"
              :label="`${$t('transfer_name')} *`"
              :error-messages="errors[0]"
              :disabled="lockedField"
              dense
            />
          </ValidationProvider>
        </v-col>
      </template>
      <template v-if="securityTransfer.transferType !== 'Internal'">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_custodian_contact_name')"
            rules="required|max:150"
            v-slot="{ errors }"
            class="flex-grow-1"
          >
            <v-text-field
              :label="`${$t('transfer_custodian_contact_name')} *`"
              v-model="securityTransfer.contactName"
              dense
              hide-details="auto"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('transfer_custodian_contact_phone')"
            v-model="securityTransfer.contactPhone"
            dense
            hide-details="auto"
            maxlength="15"
          />
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            ref="email"
            :name="$t('transfer_custodian_contact_email')"
            rules="required|email|confirmed:correo|max:100"
            v-slot="{ errors }"
            class="flex-grow-1"
          >
            <v-text-field
              :label="`${$t('transfer_custodian_contact_email')} *`"
              v-model="securityTransfer.contactEmail"
              dense
              autocomplete="nope"
              hide-details="auto"
              maxlength="100"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_custodian_confirm_email')"
            rules="required|email|max:100"
            vid="correo"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_custodian_confirm_email')"
              dense
              autocomplete="nope"
              v-model="emailConfirm"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="100"
            />
          </ValidationProvider>
        </v-col>
      </template>
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2 class="primary--text font-weight-medium">
          {{ $t('general_securities') }}
        </h2>
        <div>
          <v-btn
            color="primary"
            elevation="0"
            :loading="loading"
            @click="addSecurity"
          >
            {{ $t('add') }}
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12">
        <ValidationProvider
          :name="$t('general_securities')"
          rules="required"
          v-slot="{ errors }"
        >
          <input type="hidden" v-model="securityTransfer.securities" />
          <SecurityTable
            :securities="securityTransfer.securities"
            @remove="removeSecurity"
            @edit="editSecurity"
          />
          <span v-if="errors.length" class="error--text">{{ errors[0] }}</span>
        </ValidationProvider>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import SecurityModal from '@/components/widgets/SecurityModal'
import SecurityService from '@/services/security'
import SecurityTable from '@/components/widgets/SecurityTable'

export default {
  components: { SecurityTable },
  props: {
    securityTransfer: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isThirdParty: false,
      file: null,
      securities: [],
      loading: false,
      emailConfirm: null,
      lockedField: true,
      showBeneficiariesSelect: true
    }
  },
  computed: {
    ...mapState('account', ['account', 'accountData']),
    ...mapState('auth', ['user']),
    beneficiaries() {
      let items = []
      const main = this.accountData.firstname + ' ' + this.accountData.lastname
      items.push(main)

      if (this.accountData.type.toUpperCase() === 'JURIDICA') {
        const beneficiaries = this.accountData.beneficiaries
          .map(item => item.firstname + ' ' + item.lastname)
          .filter(item => item.trim() !== '')

        items = items.concat(beneficiaries)
      } else if (
        this.accountData.coOwners &&
        this.accountData.coOwners.trim() !== ''
      ) {
        items.push(this.accountData.coOwners)
      }

      return items
    },
    registeredIsinCodes() {
      return this.securityTransfer.securities.map(item => item.isin)
    },
    isExecutive() {
      return (
        this.user?.authorityLevelsCode === 1 ||
        this.user?.authorityLevelsCode === 2 ||
        this.user?.authorityLevelsCode === 4
      )
    },
    isAccountCode() {
      return (
        this.user?.avobjects?.findIndex(
          item =>
            item.code === this.account.code && item.typeCode === 'accountCode'
        ) > -1
      )
    }
  },
  mounted() {
    this.getSecurities()
  },
  watch: {
    'securityTransfer.transferType': {
      handler() {
        this.showBeneficiariesSelect = true
        this.isThirdParty = false
        this.lockedField = true
        this.securityTransfer.isThirdParty = false
      },
      deep: true
    }
  },
  methods: {
    async getSecurities() {
      this.loading = true
      try {
        const { data } = await SecurityService.fetch()
        this.securities = data
      } catch (error) {
        console.log(error)
        this.$dialog.message.error(this.$t('securities_data_error'))
      } finally {
        this.loading = false
      }
    },
    removeSecurity({ index }) {
      this.securityTransfer.securities.splice(index, 1)
    },
    async editSecurity({ index, item }) {
      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: this.$vuetify.breakpoint.mdAndDown ? '60%' : '50%',
        transition: 'dialog-top-transition',
        showClose: false,
        persistent: true,
        securities: this.securities,
        editSecurity: item
      }

      const result = await this.$dialog.showAndWait(SecurityModal, params)

      if (result) {
        this.securityTransfer.securities.splice(index, 1, result)
      }
    },
    async addSecurity() {
      const securities = this.securities.filter(
        item => !this.registeredIsinCodes.includes(item.isinCode)
      )

      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: this.$vuetify.breakpoint.mdAndDown ? '60%' : '50%',
        transition: 'dialog-top-transition',
        showClose: false,
        persistent: true,
        securities
      }

      const result = await this.$dialog.showAndWait(SecurityModal, params)

      if (result) {
        this.securityTransfer.securities.push(result)
      }
    },
    getSecurityTransferType(type) {
      switch (type) {
        case 'Receive':
          return this.$t('receive_security')
        case 'Send':
          return this.$t('send_security')
        default:
          return this.$t('internal_security')
      }
    },
    async handleUnlock() {
      const text = !this.isExecutive
        ? this.$t('warning_instructions_payments_to_security_transfer_customer')
        : this.$t('warning_security_transfer_to_third_parties_1') +
          ' ' +
          this.$t('warning_security_transfer_to_third_parties_2')

      const params = {
        text,
        title: this.$t('twofactor_modal_warning_title'),
        showClose: false,
        persistent: true,
        actions: {
          true: {
            text: this.$t('btn_accept'),
            color: 'primary'
          }
        }
      }

      await this.$dialog.info(params)

      if (this.isExecutive) {
        this.isThirdParty = true
        this.lockedField = false
        this.showBeneficiariesSelect = false
        this.securityTransfer.beneficiaryName = null
        this.securityTransfer.isThirdParty = this.isThirdParty

        // Set focus
        this.$nextTick(() => this.$refs.beneficiary.focus())
      }
    }
  }
}
</script>
